import { AccessPermission } from '@/models/access-permission';
import { AppNav } from '@/models/app-nav';
import { UserData, UserRole } from '@/models/user-data.model';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { AuthService } from '@auth0/auth0-angular';
import { User } from '@auth0/auth0-spa-js';
import { MessageService } from 'primeng/api';
import { environment } from 'src/environments/environment';
import { MenuService } from './menu.service';
import { DebugService } from './debug.service';

@Injectable({
  providedIn: 'root'
})
export class AppAuthService {
  private auth0 = inject(AuthService);
  userSignal = signal<User | null>(null);
  userData = signal<UserData | null>(null);
  userImage = signal<string | null>(null);
  debugService = inject(DebugService);

  largeUserImage = signal<string | null>(null);

  userPerms = signal<AccessPermission[] | null>(null);

  isLoadingSignal = signal(true);
  isAuthenticatedSignal = signal(false);

  baseUrl = environment.landmark_API_URL;

  private attemptedLogin = false;

  constructor(
    private http: HttpClient, 
    private messageService: MessageService,
    private menuService: MenuService
  ) {
    this.auth0.user$.subscribe((user) => 
      {
        this.userImage.set(user?.picture ?? '/images/generic.jpg');
        this.userSignal.set(user ?? null)
        this.getUserData().subscribe((data) => {
          // this.userImage.set('data:image/jpeg;base64,' + data.profilePicture);
          this.setUserData(data);
        });
        this.getProfilePicture().subscribe((data) => {
          this.userImage.set('data:image/jpeg;base64,' + data);
        });
  });
    this.auth0.isLoading$.subscribe((loading) => this.isLoadingSignal.set(loading));
    this.auth0.isAuthenticated$.subscribe((auth) => this.isAuthenticatedSignal.set(auth));

    effect(() => {
      const isLoading = this.isLoadingSignal();
      const isAuthenticated = this.isAuthenticatedSignal();
    
      if (!isLoading && !isAuthenticated && !this.attemptedLogin) {
        this.attemptedLogin = true;
        this.login();
      }
    });
  }

  user = computed(() => this.userSignal());

  isAuthenticated = computed(() => this.isAuthenticatedSignal());

  isLoading = computed(() => this.isLoadingSignal());


  login() {
    this.auth0.loginWithRedirect({
      authorizationParams: {
        audience: 'https://lexarhomes.api',
        scope: 'openid profile email',
        redirect_uri: `window.location.origin`
      }
    });
  }

  getUserData() {
    return this.http.get<UserData>(`${this.baseUrl}/users/me`);
  }

  getLargeUserProfileImage() {
    return this.http.get(`${this.baseUrl}/users/me/profile-picture?size=240x240`, { responseType: 'text' });
  }

  getProfilePicture() {
    return this.http.get(`${this.baseUrl}/users/me/profile-picture`, { responseType: 'text' })
  }

  logout() {
    const returnTo = document.location.origin+'/logout';
    this.auth0.logout({ logoutParams: { returnTo: returnTo } });
  }

  setUserData(userData: UserData) {
    this.menuService.populateMenu(userData);
    this.debugService.setDebugMode(userData.isDev ?? false);
    const roles = userData.UserRoles || [];
    this.userPerms.set(this.mapPerms(roles));
    userData.UserRoles = [];
    this.userData.set(userData);
  }

  mapPerms(roles:UserRole[] | undefined) {
    console.log('mapping perms', roles);
    if (!roles) return [];
    const perms = roles.map((role) => 
      role.PermRoles.RoleAccess?.map((access) => 
        access.AccessPermissions)).flat().filter((perm): perm is AccessPermission => perm !== undefined);
    console.log('perms', perms)
    return perms;
  }

  checkForPerm(table: string, type: string, fid: number) {
    const realFID = fid === 0 ? null : fid;
    const perms = this.userPerms();
    if (!perms) return false;
    return perms.some((perm) => perm.dataTable === table && perm.permType === type && (perm.FID === realFID || perm.FID === null));
  }

  updateUserData(uid: number, userData: Partial<UserData>) {
    const photo = this.userData()?.profilePicture
    const profile = this.userData()
    // Ensure profileData is initialized
    if (profile) {
    // Safely update local data
    this.userData.set({...profile, ...userData, profilePicture: photo});
    }

    // Update server-side data independently
    this.http.put<UserData>(`${this.baseUrl}/users/user/${uid}`, userData).subscribe({
      next: (updatedProfile) => {
        this.userData.set(updatedProfile);
      },
      error: (error) => {
        // Handle error by displaying a toast message
        this.messageService.add({
          severity: 'error',
          summary: 'Update Failed',
          detail: 'Failed to update user data on the server.'
        });
        console.error('Error updating user data:', error);
      }
    });
  }
}

